import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, AlertCircle, ArrowLeft, Paperclip, FileText, Image, X, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// File type icons mapping
const getFileIcon = (fileType) => {
  if (fileType?.startsWith('image/')) return Image;
  return FileText;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export default function Messages() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [match, setMatch] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Theme classes
  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const inputBg = isDark ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-400' : 'bg-white border-slate-200 text-blue-900';
  const receivedMsgBg = isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-blue-900';
  const fileBgOwn = 'bg-blue-500/30';
  const fileBgReceived = isDark ? 'bg-slate-600' : 'bg-slate-200';
  const filePreviewBg = isDark ? 'bg-slate-700' : 'bg-slate-100/50';

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [matchId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [messagesRes, matchesRes] = await Promise.all([
        axios.get(`${API}/messages/${matchId}`),
        axios.get(`${API}/matches`)
      ]);

      setMessages(messagesRes.data);
      const currentMatch = matchesRes.data.find(m => m.match_id === matchId);
      setMatch(currentMatch);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/${matchId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedFile) return;

    setSending(true);
    try {
      if (selectedFile) {
        // Upload file with message
        setUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('match_id', matchId);
        formData.append('content', newMessage.trim());
        
        await axios.post(`${API}/messages/upload-file`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        toast.success('File shared successfully');
      } else {
        // Send text message only
        const response = await axios.post(`${API}/messages`, {
          match_id: matchId,
          content: newMessage
        });

        if (response.data.flagged) {
          toast.warning('Please keep communication on-platform. Contact info detected.');
        }
      }

      setNewMessage('');
      fetchMessages();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Invalid file');
      } else {
        toast.error('Failed to send message');
      }
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadFile = async (fileName, originalName) => {
    try {
      const response = await axios.get(`${API}/messages/download-file/${fileName}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName || fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const getPartnerName = () => {
    if (!match || !user) return 'Unknown';
    return match.user1_id === user.user_id ? match.user2_name : match.user1_name;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navigation />
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className={`text-center ${textSecondary}`}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg}`} data-testid="messages-page">
      <Navigation />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <Button
            onClick={() => navigate('/matches')}
            variant="ghost"
            className={`mb-4 ${isDark ? 'text-slate-300 hover:text-white' : 'text-blue-800 hover:text-blue-900'}`}
            data-testid="back-to-matches"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Matches
          </Button>
          <h1 className={`text-3xl font-bold ${textPrimary}`}>Chat with {getPartnerName()}</h1>
        </div>

        <Card className={`shadow-sm p-6 flex flex-col ${cardBg}`} style={{ height: 'calc(100vh - 300px)' }}>
          <div className="flex-1 overflow-y-auto mb-4 space-y-4" data-testid="messages-container">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <p className={textSecondary}>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map((msg) => {
                const isOwn = msg.sender_id === user?.user_id;
                const FileIcon = getFileIcon(msg.file_type);
                return (
                  <div
                    key={msg.message_id}
                    className={`message-bubble flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${msg.message_id}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-4 ${
                        isOwn
                          ? 'bg-blue-600 text-white'
                          : receivedMsgBg
                      }`}
                    >
                      {!isOwn && (
                        <p className="text-xs opacity-70 mb-1 font-medium">{msg.sender_name}</p>
                      )}
                      
                      {/* File attachment display */}
                      {msg.file_name && (
                        <div 
                          className={`flex items-center gap-3 p-3 rounded-md mb-2 cursor-pointer hover:opacity-80 transition-opacity ${
                            isOwn ? fileBgOwn : fileBgReceived
                          }`}
                          onClick={() => handleDownloadFile(msg.file_name, msg.file_original_name)}
                        >
                          <FileIcon size={24} className={isOwn ? 'text-white' : (isDark ? 'text-blue-400' : 'text-blue-600')} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{msg.file_original_name}</p>
                            <p className="text-xs opacity-70">{formatFileSize(msg.file_size)}</p>
                          </div>
                          <Download size={16} className="flex-shrink-0 opacity-70" />
                        </div>
                      )}
                      
                      {/* Message content (if not just a file share notification) */}
                      {msg.content && !msg.content.startsWith('Shared a file:') && (
                        <p className="break-words">{msg.content}</p>
                      )}
                      
                      {msg.flagged && (
                        <div className="flex items-center gap-1 mt-2 text-xs opacity-70">
                          <AlertCircle size={12} />
                          <span>Contact info detected</span>
                        </div>
                      )}
                      <p className="text-xs opacity-70 mt-2">
                        {new Date(msg.created_at).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Selected file preview */}
          {selectedFile && (
            <div className={`flex items-center gap-3 p-3 rounded-lg mb-2 ${filePreviewBg}`}>
              <FileText size={20} className={isDark ? 'text-blue-400' : 'text-blue-500'} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm truncate ${textPrimary}`}>{selectedFile.name}</p>
                <p className={`text-xs ${textSecondary}`}>{formatFileSize(selectedFile.size)}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
                className={`p-1 ${isDark ? 'text-slate-300 hover:text-red-400' : 'text-blue-700 hover:text-red-400'}`}
              >
                <X size={16} />
              </Button>
            </div>
          )}

          <form onSubmit={handleSendMessage} className="flex gap-2" data-testid="message-form">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.txt"
            />
            
            {/* File attachment button */}
            <Button
              type="button"
              variant="ghost"
              className={`px-3 ${isDark ? 'text-slate-300 hover:text-blue-400' : 'text-blue-700 hover:text-blue-500'}`}
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || uploading}
              title="Attach file"
            >
              <Paperclip size={18} />
            </Button>
            
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={selectedFile ? "Add a message (optional)..." : "Type your message..."}
              className={`flex-1 ${inputBg}`}
              data-testid="message-input"
              disabled={sending || uploading}
            />
            <Button
              type="submit"
              className="btn-primary"
              disabled={sending || uploading || (!newMessage.trim() && !selectedFile)}
              data-testid="send-message"
            >
              {uploading ? (
                <span className="animate-spin">⏳</span>
              ) : (
                <Send size={18} />
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
